
import { connect, useSelector, useDispatch } from 'react-redux'
import { useState, useMemo } from 'react'
import { useParams, withRouter } from 'react-router-dom';

import pageWithSideMenu from '../../containers/pageWithSideMenu'
import pageContent from '../../containers/pageContent'

import { selectTool } from '../../app/store/actions/toolActions'

import { SITE_NAME } from '../../app/globals'

import toolsAPICall from '../../app/apiCalls/toolsAPICall';
import { tool_details, tool_save } from '../../app/apiEndpoints';

import Form from '../../containers/Form/Form'
import Input from '../../containers/Form/Input';
import FormFooter from "../../containers/Form/FormFooter";
import Select from "../../containers/Form/Select";
import InvalidPermissions from '../../containers/InvalidPermissions';
import ContentNotFound from '../../containers/ContentNotFound';

const ToolsForm = (props) => {
    let { toolID } = useParams();

    const [inputValue, setInputValue] = useState({ company: 0, api: 0, name: "", description: "", uri: "", apiKey: "", isAdmin: 0, isActive: 1 });
    const [errors, setErrors] = useState({ company: "", api: "", name: "", description: "", uri: "", apiKey: "", isAdmin: "", isActive: "" });
    const { company, api, name, description, uri, apiKey, isAdmin, isActive } = inputValue;
    const [formMessage, setFormMessage] = useState('')
    const [hasErrors, setHasErrors] = useState(false)
    const [loading, setLoading] = useState(true)

    const [tool, setTool] = useState({})

    const userID = useSelector((state) => state.user.id)
    const companyID = useSelector((state) => state.user.company.id)
    const isSysadmin = useSelector((state) => state.user.isSysadmin)
    const newTool = !tool.tool_id
    const canEdit = isSysadmin

    const dispatch = useDispatch();

    useMemo(() => {
        const getDetails = async () => {
            if(parseInt(toolID) > 0) {
                const data = await toolsAPICall(tool_details, {'toolID': toolID, 'requester': userID, 'company': companyID })
                if(data.tool?.company_id) {
                    const toolValues = { company: data.tool.company_id, api: data.tool.api_id, name: data.tool.tool_name, description: data.tool.description, uri: data.tool.uri, apiKey: data.tool.api_key, isActive: data.tool.isactive, isAdmin: data.tool.admin_only }
                    setInputValue(toolValues)
                    setTool(data.tool)
                } else if(data.error) {
                    setFormMessage(data.error)
                    setHasErrors(true)
                }
                if(data.found && data.found === "0") {
                    setTool({ missing: true })
                }
                setLoading(false)
            }
        }
        if(parseInt(toolID) > 0 && (tool === undefined || tool === null || !tool.tool_id) && loading) {
            getDetails()
        }
        else if(toolID === "add") {
            setLoading(false)
        }
    }, [toolID, userID, companyID, tool, loading])

    const saveTool = async (e) => {
        e.preventDefault();
        let formErrors = []
        setHasErrors(false)
        for (const field of Object.entries(errors)) {
            const error = field[1]
            if(error.length > 0) formErrors.push(error)
        }
        if(formErrors.length > 0) { 
            setHasErrors(true)
            setFormMessage(formErrors.join('<br/>'))
            return
        }
        setFormMessage('')

        const data = await toolsAPICall(tool_save, {
            'toolID': tool.tool_id ? tool.tool_id : 0, 
            'requester': userID, 
            'company': company, 
            'name': name, 
            'description': description, 
            'uri': uri, 
            'api_id': api, 
            'api_key': apiKey, 
            'isActive': isActive, 
            'admin_only': isAdmin })

        //if successful, delete from project object
        if(data.error) {
            setHasErrors(true)
            setFormMessage(data.error)
            return
        } 

        if(data.tool?.tool_id) toolID = data.tool.tool_id
        setTool(data.tool)
        
        setFormMessage("Tool has been saved")
        window.history.replaceState(null, SITE_NAME, `/tools/${toolID}`);
        dispatch(selectTool(toolID))
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputValue((prev) => ({
          ...prev,
          [name]: value,
        }));
    };

    const handleError = (field, error) => {
        setErrors((prev) => ({
            ...prev,
            [field]: error,
        }));
    };


    if(toolID === "add" && !isSysadmin)
        return <InvalidPermissions />

    if(tool.missing)
        return <ContentNotFound contentType="Tool" />

    return (
        <>
        <Form title={!newTool ? "Edit Tool" : "Add Tool"}  description="" onSubmit={saveTool} >
            { !loading ?
            <>
            <Select
                value={company}
                label="Company"
                name="company"
                formField="companies"
                useDefault={true}
                onChange={handleChange}
                onError={handleError}
                validate="numeric|required"
                colSize="6"
                readOnly={!canEdit}
            />

            <Input
                type="text"
                value={name}
                placeholder="Tool Name"
                label="Tool Name"
                name="name"
                onChange={handleChange}
                onError={handleError}
                validate="name|required"
                colSize="6"
                readOnly={!canEdit}
            />
            <Input
                type="text"
                value={description}
                placeholder="Description"
                label="Description"
                name="description"
                onChange={handleChange}
                onError={handleError}
                validate="required"
                colSize="6"
                readOnly={!canEdit}
            />
            <Input
                type="text"
                value={uri}
                placeholder="Tool URI"
                label="Tool URI"
                name="uri"
                onChange={handleChange}
                onError={handleError}
                validate="required"
                colSize="3"
                readOnly={!canEdit}
            />
            <Select
                value={api}
                label="Connected API"
                name="api"
                formField="apis"
                useDefault={true}
                onChange={handleChange}
                onError={handleError}
                validate="numeric"
                colSize="3"
                readOnly={!canEdit}
            />
            <Input
                type="text"
                value={apiKey}
                placeholder="API Key"
                label="API Key"
                name="apiKey"
                onChange={handleChange}
                onError={handleError}
                colSize="6"
                readOnly={!canEdit}
            />
            <Select
                value={isAdmin}
                label="Admin Only?"
                name="isAdmin"
                formField="yesno"
                onChange={handleChange}
                onError={handleError}
                validate="numeric"
                colSize="3"
                readOnly={!canEdit}
            />
            <Select
                value={isActive}
                label="Active?"
                name="isActive"
                formField="yesno"
                onChange={handleChange}
                onError={handleError}
                validate="numeric"
                colSize="3"
                readOnly={!canEdit}
            />
            {canEdit &&
            <FormFooter formButton={{title: "Save Tool", class: "primary"}} center={true} formMessage={formMessage} hasErrors={hasErrors} /> }
            </>
            : <p>Loading form data...</p> }
        </Form>
        </>
    )
}

const mapDispatchToProps = (dispatch) => {
    return {
        selectTool: (reportID) => { 
            dispatch(selectTool(reportID))
        }
    }
  }

export default connect(state => ({...state}),mapDispatchToProps)(pageWithSideMenu(pageContent(withRouter(ToolsForm)), "Tool Form"));
